import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useAxios } from "../../hooks";
import { User, UserSummary } from "../../models";
import { ProfileContext, IProfileContext } from ".";
import { useAuth } from "..";
import { Endpoints } from "../../api";

export const ProfileProvider = (props: PropsWithChildren) => {
    const { get } = useAxios();
    const {
        decodedAccessToken: { user_id, tenant_id },
        isLoggingIn,
        isLoggingOut,
    } = useAuth();

    const [userSummary, setUserSummary] = useState<UserSummary | undefined>(undefined);
    const [userSummaryLoading, setUserSummaryLoading] = useState<boolean>(true);
    const [impersonator, setImpersonator] = useState<User | undefined>(undefined);
    const currentUser = useMemo(() => userSummary?.user, [userSummary?.user]);

    const getUserSummary = useCallback(async () => {
        setUserSummaryLoading(true);
        const response = await get<UserSummary>({ url: Endpoints.UserSummary });
        if (response) {
            setUserSummary(response);
        }
        setUserSummaryLoading(false);
    }, [get]);

    useEffect(() => {
        getUserSummary();
    }, [getUserSummary, user_id, tenant_id]);

    const userIsLoading = useMemo(
        () => isLoggingIn || isLoggingOut || userSummaryLoading,
        [isLoggingIn, isLoggingOut, userSummaryLoading]
    );

    const value = useMemo(
        (): IProfileContext => ({
            impersonator,
            setImpersonator,
            userSummary,
            currentUser,
            userIsLoading,
        }),
        [currentUser, impersonator, userIsLoading, userSummary]
    );

    return <ProfileContext.Provider value={value} {...props} />;
};
